import { ref } from '#imports';
import { defineStore } from 'pinia';
import type { FieldsEventModal } from '~/components/widgets/modals/EventModal/EventModal.types';
import type { Hash } from 'viem';

export const useEventModalsStore = defineStore(
    'event_modals',
    () => {
        let id = 0;
        const modalsPending = ref<FieldsEventModal[]>([]);
        const modals = ref<FieldsEventModal[]>([]);

        function getNextId() {
            return ++id;
        }

        /**
         * добавляем модалку в ожидающие или отображаемые в зависимости от статуса
         * @param modal
         */
        function add(modal: FieldsEventModal) {
            removeByType('approve');
            removeByType('cancel_offer');

            // проверка на существование модалки с таким hash
            const exists = [...modals.value, ...modalsPending.value]
                .some(m => m.hash === modal.hash);

            if (exists) {
                console.warn(`Modal with hash ${modal.hash} already exists`);
                return;
            }

            const modalWithId: FieldsEventModal = {
                id: getNextId(),
                ...modal
            };

            if (modalWithId.status === 'type.pending') {
                modalsPending.value.push(modalWithId);
            }
            else {
                modals.value.push(modalWithId);
            }
        }

        /**
         * переместить модалку из ожидающей в отображение
         * @param hash
         * @param updates
         */
        function moveToModals(hash: Hash, updates: Partial<FieldsEventModal>) {
            const pendingIndex = modalsPending.value.findIndex(m => m.hash === hash);
            if (pendingIndex === -1) return null;

            const modal = modalsPending.value[pendingIndex];
            if (!modal) return null;

            const updatedModal = {
                ...modal,
                ...updates,
                id: modal.id
            };

            modals.value = [
                ...modals.value,
                ...modalsPending.value.splice(pendingIndex, 1).map(m => updatedModal)
            ];

            return updatedModal;
        }

        /**
         * обновить только статус модалки по hash (для транзакций)
         * @param hash
         * @param status
         */
        function updateStatus(hash: Hash, status: FieldsEventModal['status']) {
            const pendingIndex = modalsPending.value.findIndex(m => m.hash === hash);

            if (pendingIndex !== -1) {
                const modal = modalsPending.value[pendingIndex];
                if (modal) {
                    if (status !== 'type.pending') {
                        return moveToModals(hash, { status });
                    }
                    modal.status = status;
                    return;
                }
            }
        }

        /**
         * обновить конкретные поля модалки по hash (для транзакций)
         * @param hash
         * @param updates
         */
        function updateModal(hash: Hash, updates: Partial<FieldsEventModal>) {
            console.log(`Updating modal ${hash} with:`, updates);
            const pendingIndex = modalsPending.value.findIndex(m => m.hash === hash);
            if (pendingIndex !== -1) {
                const modal = modalsPending.value[pendingIndex];
                if (modal) {
                    modalsPending.value[pendingIndex] = {
                        ...modal,
                        ...updates,
                        id: modal.id
                    };

                    // перемещаем из пендинг в отображение, если изменился статус
                    if (updates.status && updates.status !== 'type.pending') {
                        moveToModals(hash, updates);
                    }
                    return;
                }
            }
        }

        /**
         * удаление модалки по id
         * @param id
         */
        function remove(id?: number) {
            if (!id) return;
            modals.value = modals.value.filter(modal => modal.id !== id);
            modalsPending.value = modalsPending.value.filter(modal => modal.id !== id);
        }

        /**
         * удаление модалки из отображения по типу
         * @param type
         */
        function removeByType(type: FieldsEventModal['type']) {
            modals.value = modals.value.filter(modal => modal.type !== type);
        }

        return {
            modals,
            modalsPending,
            add,
            remove,
            updateStatus,
            updateModal,
            removeByType
        } as const;
    }
);
